// src/providers/Web3Provider.js
import React from 'react';
import '@rainbow-me/rainbowkit/styles.css';
import {
  getDefaultWallets,
  RainbowKitProvider,
  connectorsForWallets,
  darkTheme,
} from '@rainbow-me/rainbowkit';
import {
  ledgerWallet,
  phantomWallet,
  braveWallet,
  injectedWallet,
  argentWallet,
  imTokenWallet,
  zerionWallet,
  okxWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { configureChains, createConfig, WagmiConfig } from 'wagmi';
import { 
  mainnet, 
  polygon, 
  arbitrum, 
  base, 
  avalanche, 
  sepolia,
  bsc,
  bscTestnet 
} from 'wagmi/chains';
import { publicProvider } from 'wagmi/providers/public';
import { jsonRpcProvider } from 'wagmi/providers/jsonRpc';
import { getEnabledChainIds, getPrimaryChain } from '../config/chainConfig';

// Get enabled chains from chainConfig
const enabledChainIds = getEnabledChainIds();
const primaryChainId = getPrimaryChain();

// Map chain IDs to wagmi chain objects
const allChains = {
  1: mainnet,
  56: bsc,
  97: bscTestnet,
  137: polygon,
  8453: base,
  42161: arbitrum,
  43114: avalanche,
  11155111: sepolia
};

// Get only enabled chains
const chains = enabledChainIds.map(id => allChains[id]).filter(Boolean);
const primaryChain = allChains[primaryChainId];

// Configure chains & providers with multi-chain support
const { chains: configuredChains, publicClient, webSocketPublicClient } = configureChains(
  chains,
  [
    // Multi-chain RPC providers
    jsonRpcProvider({
      rpc: (chain) => {
        const rpcUrls = {
          // BSC (primary)
          56: [
            'https://bsc-dataseed1.binance.org',
            'https://bsc-dataseed2.binance.org',
            'https://bsc-dataseed3.binance.org',
            'https://bsc-dataseed4.binance.org'
          ],
          97: [
            'https://data-seed-prebsc-1-s1.binance.org:8545',
            'https://data-seed-prebsc-2-s1.binance.org:8545'
          ],
          
          // Sepolia
          11155111: [
            'https://ethereum-sepolia.publicnode.com',
            'https://1rpc.io/sepolia',
            'https://rpc.sepolia.org'
          ],
          
          // Ethereum
          1: [
            'https://ethereum.publicnode.com',
            'https://1rpc.io/eth',
            'https://eth.llamarpc.com'
          ],
          
          // Polygon
          137: [
            'https://polygon.llamarpc.com',
            'https://1rpc.io/matic'
          ],
          
          // Base
          8453: [
            'https://mainnet.base.org',
            'https://1rpc.io/base'
          ],
          
          // Arbitrum
          42161: [
            'https://arbitrum.llamarpc.com',
            'https://1rpc.io/arbitrum'
          ],
          
          // Avalanche
          43114: [
            'https://1rpc.io/avax/c',
            'https://avax.network/ext/bc/C/rpc'
          ]
        };

        const urls = rpcUrls[chain.id];
        return urls ? { http: urls[0] } : null;
      },
    }),
    publicProvider(), // Fallback
  ]
);

// Get your existing default wallets
const { wallets: defaultWallets } = getDefaultWallets({
  appName: 'PhenixLoc',
  projectId: 'ebe2a405b010a90102326509e7bf3b09',
  chains: configuredChains
});

const connectors = connectorsForWallets([
  ...defaultWallets, 
  {
    groupName: 'Popular Mobile',
    wallets: [
      phantomWallet({ chains: configuredChains }), 
      braveWallet({ chains: configuredChains }), 
      imTokenWallet({ projectId: 'ebe2a405b010a90102326509e7bf3b09', chains: configuredChains }), 
      zerionWallet({ projectId: 'ebe2a405b010a90102326509e7bf3b09', chains: configuredChains }),
    ],
  },
  {
    groupName: 'Exchange Wallets',
    wallets: [
      okxWallet({ projectId: 'ebe2a405b010a90102326509e7bf3b09', chains: configuredChains }),
    ],
  },
  {
    groupName: 'Hardware & Secure',
    wallets: [
      ledgerWallet({ projectId: 'ebe2a405b010a90102326509e7bf3b09', chains: configuredChains }),
      argentWallet({ projectId: 'ebe2a405b010a90102326509e7bf3b09', chains: configuredChains }),
    ],
  },
  {
    groupName: 'DeFi',
    wallets: [
      injectedWallet({ chains: configuredChains }),
    ],
  },
]);

// Create wagmi config
const wagmiConfig = createConfig({
  autoConnect: true,
  connectors, 
  publicClient,
  webSocketPublicClient,
});

// Custom theme
const customTheme = darkTheme({
  accentColor: '#F3BA2F', 
  accentColorForeground: 'white',
  borderRadius: 'medium',
  fontStack: 'system',
});

// Global error handler
const originalError = console.error;
console.error = (...args) => {
  const errorString = args[0]?.toString() || '';
  if (errorString.includes('reverse') || 
      errorString.includes('ENS') ||
      errorString.includes('Internal error') ||
      errorString.includes('cloudflare-eth.com')) {
    return;
  }
  originalError.apply(console, args);
};

export const Web3Provider = ({ children }) => {
  return (
    <WagmiConfig config={wagmiConfig}>
      <RainbowKitProvider 
        chains={configuredChains} 
        theme={customTheme}
        appInfo={{
          appName: 'PhenixLoc',
        }}
        initialChain={primaryChain} 
      >
        {children}
      </RainbowKitProvider>
    </WagmiConfig>
  );
};

export default Web3Provider;